import React from "react";

import { faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";
import { useNavigate } from "react-router-dom";

import { FlowVersionFlowChild } from "src/api/flowTypes";
import { useDeleteFlowVersion } from "src/api/flowVersionQueries";
import { DependencyList } from "src/base-components/DependencyList";
import { TextConfirmationModal } from "src/base-components/TextConfirmationModal";
import { EmptyState } from "src/design-system/EmptyState";
import { Modal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import {
  agentVersionsToDependencyItems,
  DependencyModalSkeleton,
  flowParentsToDependencyItems,
} from "src/flow/modals/utils";
import { DraftPill, PublishedPill } from "src/flow/Pills";
import {
  useDraftAgentsDependingOnFlowVersion,
  usePublishedAgentsDependingOnFlowVersion,
} from "src/flow/useAgentDependencies";
import { useDeferredLoading } from "src/hooks/useDeferredLoading";
import { useFlowContext } from "src/router/routerContextHooks";
import { FlowPageParams, getFlowVersionsUrl } from "src/router/urls";
import { logger } from "src/utils/logger";
import { useParamsDecode } from "src/utils/useParamsDecode";

type Props = {
  isOpen: boolean;
  version?: FlowVersionFlowChild;
  onClose: () => void;
  afterLeave?: () => void;
  flowSlug: string;
};

type ModalState =
  | "loading_deps"
  | "error"
  | "has_published_deps"
  | "confirmation"
  | false;

export const DeleteVersionModal: React.FC<Props> = ({
  version,
  onClose,
  isOpen,
  afterLeave,
  flowSlug,
}) => {
  const { workspace, orgId } = useFlowContext();
  const { mutateAsync: deleteVersion } = useDeleteFlowVersion();
  const navigate = useNavigate();
  const params = useParamsDecode<FlowPageParams>();

  // Lazy fetch agent deps
  const {
    data: publishedAgentVersions,
    isError: isPublishedError,
    refetch: refetchPublished,
  } = usePublishedAgentsDependingOnFlowVersion(flowSlug, version?.id, {
    enabled: isOpen,
  });
  const {
    data: draftAgentVersions,
    isError: isDraftError,
    refetch: refetchDraft,
  } = useDraftAgentsDependingOnFlowVersion(flowSlug, version?.id, {
    enabled: isOpen,
  });

  const hasAgentDepsError = isPublishedError || isDraftError;
  const retryAgentDeps = () => {
    if (isPublishedError) refetchPublished();
    if (isDraftError) refetchDraft();
  };

  const agentDepsResolved =
    publishedAgentVersions !== undefined && draftAgentVersions !== undefined;
  const { showIndicator, suppress } = useDeferredLoading(
    !agentDepsResolved,
    isOpen,
  );

  // Published flow parent items (blocking)
  const publishedFlowItems = flowParentsToDependencyItems({
    flowVersion: version,
    orgId,
    wsId: workspace.id,
    statusFilter: "published",
  });

  // Published agent items (blocking)
  const publishedAgentItems = agentVersionsToDependencyItems(
    publishedAgentVersions ?? [],
    orgId,
    workspace.id,
    <PublishedPill />,
  );

  // FYI items: draft flow parents + draft agents
  const draftFlowItems = flowParentsToDependencyItems({
    flowVersion: version,
    orgId,
    wsId: workspace.id,
    statusFilter: "draft",
  });
  const draftAgentItems = agentVersionsToDependencyItems(
    draftAgentVersions ?? [],
    orgId,
    workspace.id,
    <DraftPill />,
  );

  const allPublishedItems = [...publishedFlowItems, ...publishedAgentItems];
  const hasPublishedDeps = allPublishedItems.length > 0;
  const fYIItems = [...draftFlowItems, ...draftAgentItems];

  const determineModalState = (): ModalState => {
    if (!isOpen) return false;
    // Separate from !isOpen: suppress hides the brief loading flash while
    // agent deps are being fetched (controlled by useDeferredLoading).
    if (suppress) return false;
    if (hasAgentDepsError) return "error";
    if (showIndicator) return "loading_deps";
    if (hasPublishedDeps) return "has_published_deps";
    if (!agentDepsResolved) return "loading_deps";
    return "confirmation";
  };

  const modal = determineModalState();

  // Only fire afterLeave when the parent is closing the modal (isOpen=false).
  // During internal state transitions (loading→confirmation), sub-modals close
  // without triggering afterLeave, which would prematurely clear the version prop.
  const resolvedAfterLeave = isOpen ? undefined : afterLeave;

  const onModalConfirm = async () => {
    if (version) {
      try {
        await deleteVersion(version.id);
        toastActions.success({ title: "Successfully deleted version" });
      } catch {
        logger.error("Error while trying to delete flow version");
      }
    }
    onClose();
    navigate(getFlowVersionsUrl(params.orgId, params.wsId, params.flow_id));
  };

  return (
    <>
      {/* Loading skeleton */}
      <Modal
        afterLeave={resolvedAfterLeave}
        open={modal === "loading_deps"}
        size="sm"
        onClose={onClose}
      >
        <Modal.Header>{`Delete version "${version?.name}"`}</Modal.Header>
        <DependencyModalSkeleton />
      </Modal>

      {/* Error loading agent deps */}
      <Modal
        afterLeave={resolvedAfterLeave}
        open={modal === "error"}
        size="sm"
        onClose={onClose}
      >
        <Modal.Header>{`Delete version "${version?.name}"`}</Modal.Header>
        <Modal.Content>
          <EmptyState
            actionButton={{ text: "Retry", onClick: retryAgentDeps }}
            description="Please try again."
            headline="Failed to load dependencies"
            icon={faTriangleExclamation}
            size="sm"
            variant="error"
          />
        </Modal.Content>
        <Modal.Footer primaryButton={false} />
      </Modal>

      {/* Blocking: published dependencies */}
      <Modal
        afterLeave={resolvedAfterLeave}
        open={modal === "has_published_deps"}
        onClose={onClose}
      >
        <Modal.Header description="This version cannot be deleted because it is used by published versions of other resources.">
          {`Cannot delete version "${version?.name}"`}
        </Modal.Header>
        <Modal.Content>
          <DependencyList
            dataLoc="version-published-dependencies"
            items={allPublishedItems}
          />
          <p className="mt-2.5 font-inter-normal-13px text-gray-500">
            Please remove these connections before deleting this version.
          </p>
        </Modal.Content>
        <Modal.Footer primaryButton={false} />
      </Modal>

      {/* Confirmation with FYI — only mount once agent deps resolved to prevent flash */}
      {agentDepsResolved && (
        <TextConfirmationModal
          additionalContent={
            fYIItems.length > 0 ? (
              <div className="mb-4">
                <DependencyList
                  dataLoc="version-dependencies"
                  items={fYIItems}
                />
              </div>
            ) : undefined
          }
          afterLeave={resolvedAfterLeave}
          challengeText={version?.name}
          confirmButtonText="Delete"
          description={
            fYIItems.length > 0
              ? "Are you sure you want to delete this version? This action cannot be undone. Deleting this version will break the connections listed below."
              : "Are you sure you want to delete this version? This action cannot be undone."
          }
          label="Please enter version name to confirm."
          open={modal === "confirmation"}
          title={`Delete version "${version?.name}"`}
          variant="danger"
          onClose={onClose}
          onConfirm={onModalConfirm}
        />
      )}
    </>
  );
};
